import { createContext, useContext, useReducer } from 'react';
import type { ReactNode } from 'react';
import { SET_GENERATION_CONFIG, SET_MODEL_CONFIG } from './actionTypes';
import type { ConfigurationState, ConfigurationAction } from '@/types';

const ConfigurationContext = createContext<ConfigurationState | null>(null);

const ConfigurationDispatchContext = createContext<React.Dispatch<ConfigurationAction> | null>(null);

const initialState: ConfigurationState = {
  generation: {
    max_new_tokens:
      parseInt(localStorage.getItem('generation_max_new_tokens') || '3072', 10) || 3072,
    temperature: 0.2,
    top_p: parseFloat(localStorage.getItem('generation_top_p') || '0.95') || 0.95,
    // num_docs: parseInt(localStorage.getItem('generation_num_docs')) || 10,
    num_docs: 10,
    typical_p: parseFloat(localStorage.getItem('generation_typical_p') || '0.95') || 0.95,
  },
  model: null,
};

interface ConfigurationProviderProps {
  children: ReactNode;
}

export function ConfigurationProvider({ children }: ConfigurationProviderProps) {
  const [configuration, dispatch] = useReducer(
    configurationReducer,
    initialState,
  );

  return (
    <ConfigurationContext.Provider value={configuration}>
      <ConfigurationDispatchContext.Provider value={dispatch}>
        {children}
      </ConfigurationDispatchContext.Provider>
    </ConfigurationContext.Provider>
  );
}

export function useConfiguration(): ConfigurationState {
  const configuration = useContext(ConfigurationContext);
  if (!configuration) {
    throw new Error('useConfiguration must be used within ConfigurationProvider');
  }
  return configuration;
}

export function useConfigurationDispatch(): React.Dispatch<ConfigurationAction> {
  const dispatch = useContext(ConfigurationDispatchContext);
  if (!dispatch) {
    throw new Error('useConfigurationDispatch must be used within ConfigurationProvider');
  }
  return dispatch;
}

function configurationReducer(configuration: ConfigurationState, action: ConfigurationAction): ConfigurationState {
  switch (action.type) {
    case SET_MODEL_CONFIG: {
      return {
        ...configuration,
        model: { ...action.payload },
      };
    }
    case SET_GENERATION_CONFIG: {
      Object.entries(action.payload).forEach(([k, v]) => {
        if (v !== undefined) {
          localStorage.setItem(`generation_${k}`, String(v));
        } else {
          localStorage.removeItem(`generation_${k}`);
        }
      });
      return {
        ...configuration,
        generation: { ...configuration.generation, ...action.payload },
      };
    }
    default: {
      throw Error('Unknown action: ' + action);
    }
  }
}
